/**
 * Controles de paginación del listado de órdenes (presentacional).
 *
 * Refleja la paginación de `GET /api/v1/orders` (`page`/`per_page`/`pages`/
 * `total`). Recibe el estado y un callback `onPageChange`; no decide datos. Los
 * botones se inhabilitan en los extremos. Pensado para complementar a
 * `OrderTable` en las pantallas de listado.
 */
import { Button } from "@/components/ui";

interface OrderPaginationProps {
  page: number;
  pages: number;
  total: number;
  onPageChange: (page: number) => void;
  /** Deshabilita los controles mientras se recarga (evita doble clic). */
  disabled?: boolean;
}

export function OrderPagination({
  page,
  pages,
  total,
  onPageChange,
  disabled = false,
}: OrderPaginationProps) {
  const safePages = Math.max(pages, 1);
  const canPrev = page > 1;
  const canNext = page < safePages;

  return (
    <nav
      className="flex items-center justify-between gap-4 px-1 py-3"
      aria-label="Paginación de órdenes"
    >
      <p className="text-sm text-slate-500">
        Página {page} de {safePages} · {total} órden{total === 1 ? "" : "es"}
      </p>
      <div className="flex gap-2">
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => onPageChange(page - 1)}
          disabled={disabled || !canPrev}
        >
          Anterior
        </Button>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => onPageChange(page + 1)}
          disabled={disabled || !canNext}
        >
          Siguiente
        </Button>
      </div>
    </nav>
  );
}
